import styled, { css } from "styled-components";

import type { AppTheme } from "../theme";
import { CornerTicks } from "./CornerTicks";

/** Lowercase `// label` section header in mono (PRD visual system). */
export const SectionLabel = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.color.textDim};
  margin-bottom: ${({ theme }) => theme.space(2)};
`;

const CardBase = styled.div<{ $ticks?: boolean; $radius: keyof AppTheme["radius"] }>`
  position: relative;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme, $radius }) => theme.radius[$radius]};
  padding: ${({ theme }) => theme.space(4)};
  ${({ $ticks }) =>
    $ticks &&
    css`
      padding: ${({ theme }) => theme.space(5)};
    `}
`;

export function Card({
  ticks,
  radius = "lg",
  children,
  className,
}: {
  ticks?: boolean;
  radius?: keyof AppTheme["radius"];
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <CardBase $ticks={ticks} $radius={radius} className={className}>
      {ticks && <CornerTicks />}
      {children}
    </CardBase>
  );
}

export const Button = styled.button<{ $variant?: "primary" | "ghost"; $full?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  font-size: 15px;
  padding: ${({ theme }) => `${theme.space(3)} ${theme.space(4)}`};
  width: ${({ $full }) => ($full ? "100%" : "auto")};
  transition: transform 0.06s ease, opacity 0.15s ease, background 0.15s ease;
  border: 1px solid transparent;

  ${({ $variant = "primary", theme }) =>
    $variant === "primary"
      ? css`
          background: ${theme.color.accent};
          color: #fff;
          box-shadow:
            0 8px 24px rgba(0, 152, 234, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.18);
          &:active {
            transform: scale(0.98);
          }
        `
      : css`
          background: transparent;
          color: ${theme.color.text};
          border-color: ${theme.color.border};
          &:active {
            transform: scale(0.98);
          }
        `}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/** Mono chip for scores / deltas / meta. */
export const Chip = styled.span<{ $tone?: "default" | "accent" | "good" | "bad" }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 5px 10px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme, $tone = "default" }) =>
    $tone === "accent" ? theme.color.accentSoft : "rgba(255, 255, 255, 0.05)"};
  border: 1px solid
    ${({ theme, $tone = "default" }) =>
      $tone === "accent" ? "rgba(0, 152, 234, 0.3)" : theme.color.border};
  color: ${({ theme, $tone = "default" }) =>
    $tone === "accent"
      ? theme.color.accent
      : $tone === "good"
        ? theme.color.good
        : $tone === "bad"
          ? theme.color.bad
          : theme.color.text};
`;

const SpinnerBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space(8)};
  color: ${({ theme }) => theme.color.textDim};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 13px;
`;

export function Loading({ label = "loading…" }: { label?: string }) {
  return <SpinnerBox>{label}</SpinnerBox>;
}

const ErrorBox = styled.div`
  margin: ${({ theme }) => theme.space(4)};
  padding: ${({ theme }) => theme.space(4)};
  border: 1px solid ${({ theme }) => theme.color.bad};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.color.text};
  background: rgba(229, 103, 91, 0.08);
  font-size: 14px;
`;

export function ErrorNotice({ message }: { message: string }) {
  return <ErrorBox role="alert">{message}</ErrorBox>;
}

/** Fixed `beta` tag pinned to the top-left corner across every screen. */
export const BetaBadge = styled.span`
  position: fixed;
  top: env(safe-area-inset-top, 0px);
  left: 0;
  z-index: 1000;
  margin: ${({ theme }) => theme.space(2)};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  padding: 3px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.color.border};
  background: ${({ theme }) => theme.color.surfaceRaised};
  color: ${({ theme }) => theme.color.accent};
  pointer-events: none;
`;
